import { humanizeNumber } from '../../common';

export function assembleFloorPlanCoreNavBtnsData( floorPlanData ) {
  if (!floorPlanData) {
    return null;
  }

  const {
    finishCollection,
    furnitureConfiguratorUrl,
    photoGalleryImages,
    roomData,
    threeD,
    threeSixties,
    twoD,
    video,
    view,
  } = floorPlanData;

  const twoDSrc = twoD?.modal;
  const threeDSrc = threeD?.modal;

  const navBtns = [];

  // Always show Info - it contains bed/bath/sqft and other essential details
  navBtns.push( {
    section: 'info-scroll',
    text: 'Info',
  } );

  if ( twoDSrc || threeDSrc ) {
    navBtns.push( {
      section: 'floorplan-scroll',
      text: 'Floor Plan',
    } );
  }

  if ( roomData?.length > 0 ) {
    navBtns.push( {
      section: 'dimensions-scroll',
      text: 'Dimensions',
    } );
  }

  if ( view?.length > 0 ) {
    navBtns.push( {
      section: 'view-scroll',
      text: 'View',
    } );
  }

  if ( photoGalleryImages?.length > 0 ) {
    navBtns.push( {
      section: 'slideshow-scroll',
      text: 'Photos',
    } );
  }

  if ( video ) {
    navBtns.push( {
      section: 'video-scroll',
      text: 'Video',
    } );
  }

  if ( threeSixties?.length > 0 ) {
    navBtns.push( {
      section: 'tour-scroll',
      text: 'Tour',
    } );
  }

  if ( furnitureConfiguratorUrl ) {
    navBtns.push( {
      section: 'configurator-scroll',
      text: 'Furniture Layout',
    } );
  }

  if ( finishCollection && Object.keys( finishCollection ).length !== 0 ) {
    navBtns.push( {
      section: 'finishes-scroll',
      text: 'Finishes',
    } );
  }

  return navBtns;
}

export function createBedBathSqFtTableData( data ) {
  if (!data) {
    return null;
  }

  const {
    bed, bath, studio, sqft, withDen,
  } = data || {};

  const tableInfo = [];

  if ( bed || bath || studio ) {
    let bedString = bed ?? '';
    bedString = studio ? 'Studio' : bedString;
    const bedBathSeperator = bedString !== '' && bath ? '/ ' : '';
    const denString = withDen === 'Yes' ? 'with Den ' : '';
    const bathString = bath || '';
    const bedBathString = `
      ${bedString}
      ${bedBathSeperator}
      ${bathString}
      ${denString}
    `;
    const bedBathInfo = {
      label: 'Bedroom & Bathroom',
      data: bedBathString,
      type: 'bedBath',
    };
    tableInfo.push( bedBathInfo );
  }

  if ( sqft ) {
    // add comma to sqft
    const humanSqFt = humanizeNumber( sqft );
    const sqftInfo = {
      label: 'Square Footage',
      data: humanSqFt,
      type: 'sqft',
    };
    tableInfo.push( sqftInfo );
  }

  return tableInfo;
}

// add the appropriate words to the bed, bath, sqft values
export function createBedBathSqftArray( data ) {
  if (!data) {
    return null;
  }

  const {
    studio,
    bed,
    bath,
    sqft,
    withDen,
  } = data || {};

  const items = [];
  if ( studio ) {
    const studioString = `Studio${withDen === 'Yes' ? ' w/ Den' : ''}`;
    items.push( studioString );
  } else {
    if ( bed ) {
      const bedString = `${bed} Bed${withDen === 'Yes' ? ' w/ Den' : ''}`;
      items.push( bedString );
    }
    if ( bath ) {
      const bathString = `${bath} Bath`;
      items.push( bathString );
    }
  }

  if ( sqft ) {
    const humanSqft = humanizeNumber( sqft );
    const sqftString = `${humanSqft} sq. ft.`;
    items.push( sqftString );
  }

  return items;
}

export function createInventoryPhrase( remainingInventory, soldOut, soldOutPhrase = 'Sold Out' ) {
  // If there are no sold out units, it's not really sold out.
  let show = false;
  if ( !soldOut || soldOut === 0 ) {
    show = false;
  } else if ( remainingInventory <= 3 ) {
    show = true;
  }
  const phrase = remainingInventory > 0 ? `${remainingInventory} Left` : soldOutPhrase;
  return show ? phrase : null;
}

// When the user has selected to display fees, set a body class to control
// display fee visibility.
export function triggerPricing( setPricingTriggered ) {
  if ( process.env.REACT_APP_IS_PRODUCTION ) {
    // Set a variable in localStorage when the user displays modal pricing.
    const EXPIRATION_TIME = 2 * 60 * 60 * 1000; // 2 hours in milliseconds.
    const now = new Date().getTime();
    const userDisplayedFeesSetting = {
      value: 'true',
      expiration: now + EXPIRATION_TIME,
    };
    localStorage.setItem( 'userDisplayedFees', JSON.stringify( userDisplayedFeesSetting ) );
  }

  setPricingTriggered( true );
}

export function resetTriggerPricing( setPricingTriggered ) {
  const displayedFees = JSON.parse( localStorage.getItem( 'userDisplayedFees' ) );
  if ( displayedFees ) {
    localStorage.removeItem( 'userDisplayedFees' );
  }
  setPricingTriggered( false );
}

export function showPricingTrigger(
  pricingTriggered,
  platformShowPricing,
  platformShowPricingInitially,
  hasFees,
) {
  return !pricingTriggered && platformShowPricing && !platformShowPricingInitially && hasFees;
}

export function assembleFloorPlanHeaderTextData(
  data,
  existingBedBathSqFtArray,
  existingInventoryPhrase,
  hasInternalHeader,
  rawShowPricingTrigger,
  setPricingTriggered,
  components,
  soldOutPhrase,
) {
  if (!data) {
    return null;
  }

  const headerText = {
    titleParts: [],
    tags: [],
    subtitles: [],
    btns: [],
    components: components || [],
  };

  const {
    levelOfCareName,
    levelsOfCare,
    floorPlanCategoryName,
    remainingInventory,
    soldOut,
    floorPlanTitle,
  } = data || {};

  // Residence payloads carry a scalar levelOfCareName; floor plan payloads
  // carry a multi-value levelsOfCare array. Coalesce to one display string.
  const displayLocName = levelOfCareName
    || (Array.isArray(levelsOfCare) && levelsOfCare.length
      ? levelsOfCare.map((loc) => loc.name).join(', ')
      : null);

  if ( floorPlanTitle ) {
    headerText.titleParts.push(floorPlanTitle);
  }

  // Assemble header text for internal header (PIB)
  if ( hasInternalHeader ) {
    // Separate entries, not one joined string: DetailHeader renders each
    // subtitle in its own span with a bullet separator, so a multi-LoC plan
    // reads "Assisted Living, Memory Care" then "Studio" rather than running
    // the category onto the end of the last level-of-care name.
    if ( displayLocName ) {
      headerText.subtitles.push( displayLocName );
    }
    if ( floorPlanCategoryName ) {
      headerText.subtitles.push( floorPlanCategoryName );
    }
  } else {
    // Assemble header text for modal header.
    headerText.subtitles = !existingBedBathSqFtArray
      ? createBedBathSqftArray( data ) : existingBedBathSqFtArray;
  }

  // Add inventory tag - doesn't show for Residences since they don't have
  // remainingInventory.
  const inventoryPhrase = existingInventoryPhrase
    || createInventoryPhrase( remainingInventory, soldOut, soldOutPhrase );

  if ( inventoryPhrase ) {
    headerText.tags.push({
      type: 'soldOut',
      text: inventoryPhrase,
    });
  }

  // Add Pricing toggle.
  if ( rawShowPricingTrigger ) {
    headerText.btns.push({
      text: 'more',
      className: 'detailHeader__subtitleGroup detailHeader__subtitleGroup--btn',
      onClick: () => { triggerPricing( setPricingTriggered ); },
    });
  }

  return headerText;
}

export const FLOOR_PLAN_API_INCLUDES = [
  'field_2d_floor_plan',
  'field_3d_floor_plan',
  'field_level_of_care',
  'field_floor_plan_category',
  'field_fee_groups',
  'field_fee_groups.paragraph_type',
  'field_fee_groups.field_financial_plans',
  'field_finish_collection',
  'field_finish_collection.field_finish_option',
  'field_finish_collection.field_finish_option.field_finish_space',
  'field_finish_collection.field_finish_option.field_finish_palette',
  'field_finish_collection.field_finish_option.field_media_image',
  'field_virtual_tour',
  'field_virtual_tour.field_three_sixties',
  'field_finish_collection',
  'field_features',
  'field_photo_gallery',
  'field_photo_gallery.field_gallery_photos',
  'field_video',
  'field_downloadable_pdf',
];

export const createFloorPlanApiRequest = ( uuid ) => {
  const request = {
    entityType: 'node',
    contentType: 'floor_plan',
    uuid,
    params: {
      include: FLOOR_PLAN_API_INCLUDES.join( ',' ),
    },
  };
  return request;
};
